import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getMyAccountMenuItems } from '../../constants/menuItems';
import { useCreateStatementPdf } from '../../hooks/apolloHooks/useCreateStatementPdf';
import { useGetAllCards } from '../../hooks/apolloHooks/useGetAllCards';
import { useGetCardInvoicesDeliveryTypes } from '../../hooks/apolloHooks/useGetCardInvoicesDeliveryTypes';
import { useGetCardInvoicesPaidRemains } from '../../hooks/apolloHooks/useGetCardInvoicesPaidRemains';
import { useGetCustomer } from '../../hooks/apolloHooks/useGetCustomer';
import { GET_CARD_INVOICES_QUERY, GET_CARD_STATEMENTS_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { CardInvoice, CardInvoicesCustomerDataResponse, CardStatementsCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';
import { StatementPdfData } from '../../integrations/allkort/graphql/interface/StatementPdf';
import { getOverdueInvoices } from '../../utils/invoiceDateUtils';
import { scrollToElement } from '../../utils/scrollUtils';
import FetchingAnimation from '../elements/FetchingAnimation';
import InvoiceTableRow from '../elements/invoicesstatements/InvoiceTableRow';
import SmartcardStatementsGridHeader from '../elements/invoicesstatements/SmartcardStatmentsGridHeader';
import StatementGridSingle from '../elements/invoicesstatements/StatmentGridSingle';
import SecondMenu from '../elements/SecondMenu';
import { SearchCrossIcon } from '../svgs/ExpressonSVGs';
import InvoicesStatementsHeader from '../views/Invoicesstatements/InvoicesStatementsHeader';

// Grace period in days before an invoice is considered overdue
// An invoice is only overdue if the due date has passed by this many days
const OVERDUE_GRACE_PERIOD_DAYS = 1;

// InvoicesPage fetches its own data - no props needed
const InvoicesPage: React.FC = () => {
	const invoiceWrapperClasses = 'white-pale-section shadow-one';
	const location = useLocation();

	// State for invoice list with pagination
	const [allInvoices, setAllInvoices] = useState<CardInvoice[]>([]);
	const [invoiceCursor, setInvoiceCursor] = useState<string | null>(null);
	const [lastProcessedCursor, setLastProcessedCursor] = useState<string | null>(null);
	const [displayInvoiceCount, setDisplayInvoiceCount] = useState(8); // Number of invoices to display initially

	// State for statement list with pagination
	const [allStatements, setAllStatements] = useState<any[]>([]);
	const [statementCursor, setStatementCursor] = useState<string | null>(null);
	const [lastProcessedStatementCursor, setLastProcessedStatementCursor] = useState<string | null>(null);

	const { cards, isComplete } = useGetAllCards()
	const cardTypes = new Set(cards.map(card => card.type.shortName))

	// Check which card types customer has which is synonym to if they have invoice and statements services
	let hasP = cardTypes.has('P')  // Smartkort
	let hasK = cardTypes.has('K')  // Tankkort/Företagskort



	// Set default active tab: Invoice tab if hasK, otherwise Statement tab
	const [invoiceTabActive, setInvoiceTabActive] = useState(hasK); // true = Invoices, false = Statement

	// Update hasP and hasK when cards loading is complete and update active tab
	useEffect(() => {
		if (isComplete) {
			// Update hasP and hasK based on loaded cards
			hasP = cardTypes.has('P');
			hasK = cardTypes.has('K');

			// Only set default tab if there's no hash in URL
			const hash = location.hash;
			if (!hash || (hash !== '#invoices' && hash !== '#statements')) {
				setInvoiceTabActive(hasK); // Set to Invoices tab if hasK, otherwise Statements
			}
		}
	}, [isComplete, location.hash]);

	// Handle URL hash navigation on component mount
	useEffect(() => {
		const hash = location.hash;

		if (hash === '#invoices') {
			setInvoiceTabActive(true);
		} else if (hash === '#statements') {
			setInvoiceTabActive(false);
		}
	}, [location.hash]);

	// Handle scrolling after data is loaded
	useEffect(() => {
		const hash = location.hash;

		if (hash === '#invoices' && invoiceTabActive && allInvoices.length > 0) {
			// Scroll to invoice list once invoices are loaded
			setTimeout(() => {
				scrollToElement('invoice-list');
			}, 100);
		} else if (hash === '#statements' && !invoiceTabActive && allStatements.length > 0) {
			// Scroll to statements list once statements are loaded
			setTimeout(() => {
				scrollToElement('invoice-list');
			}, 100);
		}
	}, [location.hash, invoiceTabActive, allInvoices.length, allStatements.length]);

	// Variables for invoice pagination (supports Load More button)
	// Fetch 20 invoices initially to ensure all unpaid/overdue invoices are captured for counting
	// But only display 6-8 rows in the table (pagination handled in JSX)
	const invoiceVariables = useMemo(() => ({
		first: invoiceCursor ? 6 : 20,  // Initial: 20 (for counting), Load More: 6 (for display)
		after: invoiceCursor,
		before: null,
		last: null
	}), [invoiceCursor]);

	// Fetch invoices with manual pagination for Load More button support
	const { data: invoicesData, loading: invoicesLoading, error: invoicesError } = useGetCustomer<CardInvoicesCustomerDataResponse>(
		GET_CARD_INVOICES_QUERY,
		invoiceVariables,
		{ fetchPolicy: 'network-only' }
	);

	// Process invoice data
	useEffect(() => {
		if (!invoicesData?.currentCustomer?.cardInvoices) return;

		const { nodes, pageInfo } = invoicesData.currentCustomer.cardInvoices;

		if (nodes && nodes.length > 0) {
			// Only process if cursor hasn't been processed yet
			const currentCursor = pageInfo?.endCursor;
			if (lastProcessedCursor === currentCursor) return;

			setAllInvoices(prev => {
				const existingIds = new Set(prev.map(inv => inv.id));
				const merged = invoiceCursor
					? [...prev, ...nodes.filter(inv => !existingIds.has(inv.id))]
					: [...nodes];
				// Always sort by invoiceDate descending so newest is first,
				// regardless of the order the API returns batches
				return merged.sort((a, b) =>
					new Date(b.invoiceDate).getTime() - new Date(a.invoiceDate).getTime()
				);
			});

			setLastProcessedCursor(currentCursor);
		}
	}, [invoicesData, invoiceCursor, lastProcessedCursor]);

	// Fetch all paid/remains data with auto-pagination
	const { paidRemains, isComplete: paidRemainsComplete } = useGetCardInvoicesPaidRemains();

	// Fetch all delivery types with auto-pagination TODO: do we want to use this somewhere?
	const { deliveryTypes } = useGetCardInvoicesDeliveryTypes();

	// Variables for statement pagination (supports Load More button)
	// Fetch the first 6 statements initially and load the next pages using after/endCursor.
	const statementVariables = useMemo(() => ({
		first: 6,
		after: statementCursor,
		before: null,
		last: null
	}), [statementCursor]);

	// Fetch statements with all fields (balance, payment, purchase, ocr, statementDate, deliveryType)
	const { data: statementsData, loading: statementsLoading, error: statementsError } = useGetCustomer<CardStatementsCustomerDataResponse>(
		GET_CARD_STATEMENTS_QUERY,
		statementVariables,
		{ fetchPolicy: 'network-only' }
	);

	// Process statement data
	useEffect(() => {
		if (!statementsData?.currentCustomer?.prepaidStatements) return;

		const { nodes, pageInfo } = statementsData.currentCustomer.prepaidStatements;

		if (nodes && nodes.length > 0) {
			// Only process if cursor hasn't been processed yet
			const currentCursor = pageInfo?.endCursor;
			if (lastProcessedStatementCursor === currentCursor) return;

			setAllStatements(prev => {
				const existingIds = new Set(prev.map(stmt => stmt.id));
				const newStatements = nodes.filter(stmt => !existingIds.has(stmt.id));
				return [...prev, ...newStatements].sort((a, b) =>
					new Date(b.statementDate).getTime() - new Date(a.statementDate).getTime()
				);
			});

			setLastProcessedStatementCursor(currentCursor);
		}
	}, [statementsData, statementCursor, lastProcessedStatementCursor]);

	// Get latest statement for PDF generation (use accumulated statements)
	const latestStatement = allStatements.length > 0 ? allStatements[0] : null;

	// Track if statements fetch is complete and no statements exist
	const hasNoStatements = !statementsLoading && !statementsError && allStatements.length === 0;

	// Create statement PDF mutation hook
	const { createStatementPdf, loading: statementPdfLoading } = useCreateStatementPdf();

	// State to hold the generated statement PDF data
	const [latestStatementPdfData, setLatestStatementPdfData] = useState<StatementPdfData | null>(null);

	// Generate PDF for latest statement when data is available
	useEffect(() => {
		if (latestStatement && !latestStatementPdfData && !statementPdfLoading) {
			const generatePdf = async () => {
				try {
					const pdfData = await createStatementPdf(latestStatement.id);
					if (pdfData) {
						setLatestStatementPdfData(pdfData);
					}
				} catch (error) {
					console.error('Failed to generate statement PDF:', error);
				}
			};
			generatePdf();
		}
	}, [latestStatement, latestStatementPdfData, statementPdfLoading, createStatementPdf]);

	// Handle loading state TODO: improve with better loading UI
	if (invoicesLoading && allInvoices.length === 0) {
		return (
			<div className="skeleton-invoice-page">
				<FetchingAnimation width={644} maxWidth={true} height={151} marginBottom={20} marginTop={15} />
				<div className="skeleton-page-content-wrapper">
					<FetchingAnimation width={'100'} maxWidth={true} height={500} marginBottom={10} marginTop={15} />
				</div>
			</div>);
	}
	if (invoicesError) return <div>Error fetching invoices: {invoicesError.message}</div>


	// Get the latest invoice if available
	const latestInvoice = allInvoices.length > 0 ? allInvoices[0] : null;

	// Get invoice count from the first fetch
	const invoiceCount = invoicesData?.currentCustomer?.cardInvoices?.nodes?.length || 0;

	// Enrich latest invoice with payment details if available
	// This is only for display in the header the first invoice
	let enrichedLatestInvoice = latestInvoice;
	if (latestInvoice && paidRemains.length > 0) {
		const paymentInfo = paidRemains.find(item => item.id === latestInvoice.id);
		if (paymentInfo) {
			enrichedLatestInvoice = {
				...latestInvoice,
				paid: paymentInfo.paid,
				remains: paymentInfo.remains
			};
		}
	}

	// Handler for Load More button
	// Increase the display count and fetch the next invoice page when needed.
	const handleLoadMore = () => {
		const nextDisplayCount = displayInvoiceCount + 6;
		const hasMoreLoadedInvoices = allInvoices.length > displayInvoiceCount;

		setDisplayInvoiceCount(nextDisplayCount);

		if (!hasMoreLoadedInvoices && invoicesData?.currentCustomer?.cardInvoices?.pageInfo?.hasNextPage) {
			setInvoiceCursor(invoicesData.currentCustomer.cardInvoices.pageInfo.endCursor);
		}
	};

	// Check if we should show Load More button
	// Show it when more loaded invoices exist or when the API has more pages.
	const shouldShowLoadMoreInvoices = allInvoices.length > displayInvoiceCount
		|| invoicesData?.currentCustomer?.cardInvoices?.pageInfo?.hasNextPage;

	// Handler for Load More Statements button
	const handleLoadMoreStatements = () => {
		if (statementsData?.currentCustomer?.prepaidStatements?.pageInfo?.hasNextPage) {
			setStatementCursor(statementsData.currentCustomer?.prepaidStatements?.pageInfo.endCursor);
		}
	};

	// Helper to check if an invoice is unpaid
	// An invoice is unpaid if: remains > 0 OR remains is null (unknown/unpaid)
	const isInvoiceUnpaid = (inv: any) => {
		return inv.remains === null || (inv.remains && inv.remains > 0);
	};

	// Get unpaid invoices count
	const unpaidInvoices = paidRemains.filter(isInvoiceUnpaid);
	const unpaidInvoicesCount = unpaidInvoices.length;

	// Get unpaid invoices whose due date is past the grace period
	const overdueInvoices = getOverdueInvoices(
		unpaidInvoices,
		allInvoices,
		new Date(),
		OVERDUE_GRACE_PERIOD_DAYS
	);
	const overdueInvoicesCount = overdueInvoices.length;

	const totalOverdueSum = overdueInvoices.reduce((sum, inv) => {
		const amount = inv.remains !== null ? inv.remains : 0;
		return sum + amount;
	}, 0);

	// Get the newest unpaid invoice with full data (last in unpaidInvoices array = newest)
	const firstUnpaidInvoiceData = unpaidInvoices.length > 0 ? unpaidInvoices[unpaidInvoices.length - 1] : null;
	const firstUnpaidInvoice = firstUnpaidInvoiceData && allInvoices.length > 0
		? allInvoices.find(inv => inv.id === firstUnpaidInvoiceData.id)
		: null;

	// Enrich first unpaid invoice with payment data if available
	const enrichedFirstUnpaidInvoice = firstUnpaidInvoice && firstUnpaidInvoiceData
		? {
			...firstUnpaidInvoice,
			paid: firstUnpaidInvoiceData.paid,
			remains: firstUnpaidInvoiceData.remains
		}
		: null;

	// Generate menu items dynamically based on customer's card types
	const menuItems = getMyAccountMenuItems(hasP, hasK);

	return (
		<>
			<SecondMenu menuItems={menuItems} />
			<div className="Section invoices-page-component">
				<div className="Row Row--JustifyLeft">
					<InvoicesStatementsHeader
						wrapperClasses={invoiceWrapperClasses}
						latestInvoice={enrichedLatestInvoice}
						unpaidInvoicesCount={unpaidInvoicesCount}
						invoiceCount={invoiceCount}
						firstUnpaidInvoice={enrichedFirstUnpaidInvoice}
						totalOverdueSum={totalOverdueSum}
						overdueInvoicesCount={overdueInvoicesCount}
						latestStatementPdfData={latestStatementPdfData}
						hasNoStatements={hasNoStatements}
						invoiceTabActive={invoiceTabActive}
						setInvoiceTabActive={setInvoiceTabActive}
						paidRemainsComplete={paidRemainsComplete}
					/>

					{/* This section below is the invoice and statements lists */}
					<div
						className={classNames(
							'Row__Inner',
							'inv-state-content',
							'white-pale-section',
							'shadow-one'
						)}
						id="invoice-list"
					>
						<div className={classNames(
							'inv-state-list-selector',
							!invoiceTabActive ? 'statements-active' : 'invoices-active',
						)}>
							{hasK && (
								<button className={invoiceTabActive ? "active" : ""} onClick={() => setInvoiceTabActive(true)}>
									Fakturor
								</button>
							)}

							{hasP && (
								<button className={!invoiceTabActive ? "active" : ""} onClick={() => setInvoiceTabActive(false)}>
									Kontobesked
								</button>
							)}
						</div>

						{/* Show invoices or statements depending on active tab */}
						{invoiceTabActive ? (
							<>
								<div className="invoice-table-wrapper">
									{allInvoices.length !== 0 && (
										<div className="invoice-table-wrapper__header has-dark-bg">
											<span className="header-invoicenr">Fakturanr</span>
											<span className="header-date">Fakturadatum</span>
											<span className="header-due">Förfallodatum</span>
											<span className="header-amount">Belopp</span> {/* TODO: Here we may have the remaining amount? */}
											<span className="header-status">Status</span>
											<span className="header-action">Åtgärd</span>
										</div>
									)}

									<div className="invoice-table-wrapper__list">
										{allInvoices.length === 0 ? (
											<div className="no-invoices-message">
												<p><SearchCrossIcon width={24} height={24} />Du har inga registrerade fakturor.</p>
											</div>
										) : !paidRemainsComplete ? (
											<>
												<FetchingAnimation width={'100'} maxWidth={true} height={64} marginBottom={2} marginTop={0} />
												<FetchingAnimation width={'100'} maxWidth={true} height={64} marginBottom={2} marginTop={0} />
												<FetchingAnimation width={'100'} maxWidth={true} height={64} marginBottom={2} marginTop={0} />
												<FetchingAnimation width={'100'} maxWidth={true} height={64} marginBottom={2} marginTop={0} />
												<FetchingAnimation width={'100'} maxWidth={true} height={64} marginBottom={2} marginTop={0} />
											</>
										) : (
											allInvoices.slice(0, displayInvoiceCount).map(invoice => {
												// Enrich invoice with payment data
												const paymentInfo = paidRemains.find(item => item.id === invoice.id);
												const enrichedInvoice = paymentInfo
													? { ...invoice, paid: paymentInfo.paid, remains: paymentInfo.remains }
													: invoice;

												return <InvoiceTableRow key={invoice.id} invoice={enrichedInvoice} />;
											})
										)}
									</div>
								</div>


								{/* Load More Button */}
								{shouldShowLoadMoreInvoices && (
									<div className="Row Row--JustifyCenter" style={{ marginTop: '20px' }}>
										<button
											onClick={handleLoadMore}
											disabled={invoicesLoading}
											className="Button load-more-button body-text"
										>
											{invoicesLoading ? 'Laddar...' : 'Ladda in fler fakturor'}
										</button>
									</div>
								)}
							</>
						) : (
							<>
								<div className="statements-table-wrapper">
									<div className="statements-table-wrapper__grid">
										<SmartcardStatementsGridHeader />
										{allStatements.length === 0 ? (
											<div className="no-statements-message">
												<p><SearchCrossIcon width={24} height={24} />Inga kontobesked hittades.</p>
											</div>
										) : (
											allStatements.map(statement => {
												return <StatementGridSingle key={statement.id} statement={statement} />;
											})
										)}
									</div>
								</div>

								{/* Load More Button for Statements */}
								{statementsData?.currentCustomer?.prepaidStatements?.pageInfo?.hasNextPage && (
									<div className="Row Row--JustifyCenter" style={{ marginTop: '20px' }}>
										<button
											onClick={handleLoadMoreStatements}
											disabled={statementsLoading}
											className="Button load-more-button body-text"
										>
											{statementsLoading ? 'Laddar...' : 'Ladda in fler kontobesked'}
										</button>
									</div>
								)}
							</>
						)}
					</div>
				</div>
			</div>
		</>
	)
}
export default InvoicesPage
